import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import moment from "moment/moment";

import { firestore } from "../../config/ConfigFirebase";

// Cursor-paginated readers for the radicados lists, plus the text helpers the
// search UI needs. Mirrors fetchPqrsdPage in PqrsdService.js — same collection,
// same cursor contract — but keeps createdAt as a moment because FiledItem
// renders it with .format(); PQRSD flattens to ISO only because its rows live
// in Redux.
const FILED = "filed";
const REQUESTS_FILED = "requestsFiled";

// Accent folding, length-preserving on purpose: match offsets found in the
// folded string are used to slice the ORIGINAL text for the snippet, so the
// user reads their document back with its real casing and accents. NFD +
// combining-mark strip would break that alignment on pre-decomposed input.
const FOLD = {
  á: "a", à: "a", ä: "a", â: "a", ã: "a",
  é: "e", è: "e", ë: "e", ê: "e",
  í: "i", ì: "i", ï: "i", î: "i",
  ó: "o", ò: "o", ö: "o", ô: "o", õ: "o",
  ú: "u", ù: "u", ü: "u", û: "u",
  ñ: "n", ç: "c", ý: "y", ÿ: "y",
};

const FOLD_RE = /[áàäâãéèëêíìïîóòöôõúùüûñçýÿ]/g;

// Per-character fallback used only when the fast path changes the string
// length (exotic casing rules such as "İ" lowercasing to two code units).
// Never expands: a char that would grow is left untouched.
const foldSafe = (value) =>
  Array.from(value)
    .map((ch) => {
      const lower = ch.toLowerCase();
      const single = lower.length === ch.length ? lower : ch;
      return FOLD[single] || single;
    })
    .join("");

export const foldText = (value) => {
  const source = String(value ?? "");
  const folded = source.toLowerCase().replace(FOLD_RE, (c) => FOLD[c]);
  return folded.length === source.length ? folded : foldSafe(source);
};

// Collapse first, fold second — both copies then share one index space.
const collapse = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

// senders / recipients / carbonCopy hold person objects, but SelectUser is
// freeSolo so a hand-typed entry arrives as a bare string.
const personText = (person) => {
  if (!person) return "";
  if (typeof person === "string") return person;
  return [
    person.name,
    person.lastName,
    person.email,
    person.position,
    person.entity,
    person.dependence,
  ]
    .filter(Boolean)
    .join(" ");
};

// Built once per row at map time so filtering on every keystroke is a plain
// substring scan over one prepared string instead of re-walking the document.
export const buildSearchIndex = (data) => {
  const meta = collapse(
    [
      data.filedId,
      data.referenceId,
      data.subject,
      data.externalFiled,
      ...(data.senders || []).map(personText),
      ...(data.recipients || []).map(personText),
      ...(data.carbonCopy || []).map(personText),
    ]
      .filter(Boolean)
      .join(" · ")
  );
  const docText = collapse(data.documentText);
  const searchBlob = foldText(docText ? `${meta} ${docText}` : meta);
  const docTextOffset = docText ? meta.length + 1 : -1;
  // Guard the invariant the snippet slicing depends on. If anything ever
  // desynchronises the two copies we still match, we just stop offering
  // snippets for that row rather than quoting the wrong passage.
  const aligned = docTextOffset < 0 || searchBlob.length === meta.length + 1 + docText.length;
  return {
    searchBlob,
    docText,
    docTextOffset: aligned ? docTextOffset : -1,
    hasDocumentText: !!docText,
  };
};

export const parseSearchTokens = (search) =>
  foldText(collapse(search)).split(" ").filter(Boolean);

// AND across tokens so "juan perez" finds "Pérez, Juan". Substring rather than
// word-boundary matching because OCR splits and joins words unpredictably.
export const matchesTokens = (row, tokens) =>
  tokens.every((token) => row.searchBlob.includes(token));

// Only returns a snippet when the hit lives exclusively in the document text —
// if the term is already visible in the asunto or the remitente, the card
// explains itself and a quote would just be noise.
export const buildMatchSnippet = (row, tokens, radius = 60) => {
  if (!row || row.docTextOffset < 0) return null;
  const metaPart = row.searchBlob.slice(0, row.docTextOffset);
  for (const token of tokens) {
    if (metaPart.includes(token)) continue;
    const hit = row.searchBlob.indexOf(token, row.docTextOffset);
    if (hit < 0) continue;
    const local = hit - row.docTextOffset;
    const start = Math.max(0, local - radius);
    const end = Math.min(row.docText.length, local + token.length + radius);
    return {
      before: row.docText.slice(start, local),
      match: row.docText.slice(local, local + token.length),
      after: row.docText.slice(local + token.length, end),
      leading: start > 0,
      trailing: end < row.docText.length,
    };
  }
  return null;
};

const toMoment = (value) => {
  if (!value) return moment(null);
  if (moment.isMoment(value)) return value;
  return moment(value.toDate ? value.toDate() : value);
};

const toDate = (value) => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  return new Date(value.toDate ? value.toDate() : value);
};

// The raw documentText is dropped: docText supersedes it as the render source
// and keeping both would double the OCR footprint for every loaded row.
const mapRow = (source, fallbackId) => {
  const { searchBlob, docText, docTextOffset, hasDocumentText } = buildSearchIndex(source);
  const { documentText, request, participants, ...rest } = source;
  return {
    ...rest,
    filedId: source.filedId || fallbackId,
    createdAt: toMoment(source.createdAt),
    externalDate: toDate(source.externalDate),
    searchBlob,
    docText,
    docTextOffset,
    hasDocumentText,
  };
};

const mapFiledRow = (snap) => mapRow(snap.data(), snap.id);

// requestsFiled wraps the whole radicado under `request`. The spread order and
// the createdAt source match getRequestsFiledListOnSnapshot so the card keeps
// showing the radicado date, not the finalisation date.
const mapRequestsFiledRow = (snap) => {
  const data = snap.data();
  return mapRow({ ...data, ...data.request }, snap.id);
};

export const fetchFiledPage = async ({
  filedType,
  dateFrom,
  dateTo,
  cursor,
  pageSize = 25,
} = {}) => {
  try {
    const constraints = [];
    // filedType stores a correspondenceId, which is exactly what the radio
    // holds — so this filter runs server-side against the existing
    // filedType + createdAt index instead of thinning an already-read page.
    if (filedType && filedType !== "all") constraints.push(where("filedType", "==", filedType));
    if (dateFrom) constraints.push(where("createdAt", ">=", dateFrom));
    if (dateTo) constraints.push(where("createdAt", "<=", dateTo));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    // One extra row tells us whether more exist without a second count query.
    constraints.push(limit(pageSize + 1));

    const snap = await getDocs(query(collection(firestore, FILED), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapFiledRow),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchFiledPage error:", e);
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};

// Date bounds here apply to the outer createdAt, which addRequestsFiled sets to
// doneAt — so the range filters by finalisation date while the card shows the
// radicado date. Pre-existing behaviour, kept deliberately.
export const fetchRequestsFiledPage = async ({
  userId,
  dateFrom,
  dateTo,
  cursor,
  pageSize = 25,
} = {}) => {
  if (!userId) return { items: [], lastDoc: null, hasMore: false };
  try {
    const constraints = [where("participants", "array-contains", userId)];
    if (dateFrom) constraints.push(where("createdAt", ">=", dateFrom));
    if (dateTo) constraints.push(where("createdAt", "<=", dateTo));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1));

    const snap = await getDocs(query(collection(firestore, REQUESTS_FILED), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapRequestsFiledRow),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchRequestsFiledPage error:", e);
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};
